import type { EvidenceProps } from './registry';

interface TagRecord {
  id?: number;
  name?: string;
  trackingStatus: string;
  hasRecentActivity: boolean;
  description?: string;
}

interface UetConfigurationEvidenceShape {
  totalTags: number;
  activeCount: number;
  inactiveCount: number;
  unverifiedCount: number;
  active: TagRecord[];
  inactive: TagRecord[];
  unverified: TagRecord[];
  summaryMessage: string;
}

const STATUS_LABEL: Record<string, string> = {
  Active: 'Active',
  Inactive: 'Inactive',
  Unverified: 'Unverified'
};

function statusLabel(s: string): string {
  return STATUS_LABEL[s] ?? s;
}

export const UetConfigurationEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as UetConfigurationEvidenceShape;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>{e.summaryMessage}</p>

      {e.totalTags === 0 ? (
        <p className='evidence__footnote'>
          To set up UET, generate a tag in Microsoft Ads UI under{' '}
          <code>Tools › Conversion tracking › UET tag</code> and install the snippet on every page
          of the site. Conversion goals link to UET tags, so this is the prerequisite for all
          web-based conversion tracking.
        </p>
      ) : (
        <>
          {e.activeCount > 0 && (
            <UetSection label='Active' count={e.activeCount} kind='ok' tags={e.active} />
          )}
          {e.inactiveCount > 0 && (
            <UetSection label='Inactive' count={e.inactiveCount} kind='warn' tags={e.inactive} />
          )}
          {e.unverifiedCount > 0 && (
            <UetSection
              label='Unverified'
              count={e.unverifiedCount}
              kind='warn'
              tags={e.unverified}
            />
          )}
        </>
      )}
    </div>
  );
};

interface UetSectionProps {
  label: string;
  count: number;
  kind: 'ok' | 'warn';
  tags: TagRecord[];
}

const UetSection = ({ label, count, kind, tags }: UetSectionProps) => {
  const tagClass = kind === 'ok' ? 'evidence__section-tag--ok' : 'evidence__section-tag--context';
  return (
    <section className='evidence__section'>
      <h4 className='evidence__section-title'>
        {label} UET tags
        <span className={`evidence__section-tag ${tagClass}`}>{count}</span>
      </h4>
      <table className='evidence__table evidence__table--uet'>
        <thead>
          <tr>
            <th>Tag name</th>
            <th>Status</th>
            <th>Recent activity</th>
            <th className='evidence__th--right'>Tag ID</th>
          </tr>
        </thead>
        <tbody>
          {tags.map(t => (
            <tr key={t.id ?? t.name}>
              <td>
                <div>{t.name ?? '(unnamed)'}</div>
                {t.description && <div className='evidence__action-id'>{t.description}</div>}
              </td>
              <td>{statusLabel(t.trackingStatus)}</td>
              <td>{t.hasRecentActivity ? 'Yes' : 'No'}</td>
              <td className='evidence__td--right'>{t.id ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};
